import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import {
  isPending,
  statusLabel,
  indexWarnings,
  warningsForLine,
  reviewSummary,
  countLines,
  buildDraftUpdate,
  emptyBrief,
  mergeRecommendation,
  carriedBriefFields,
  briefRequest,
  recommendationNotice,
  noticeClass,
  SEQUENCE_OPTIONS,
  recommendRequest,
  sequenceReasoningLine,
  describeSqPosition,
  editBriefField,
  pendingHint,
  nextPollDelay,
  pollFailure,
  POLL_FAILURES_BEFORE_NOTICE,
  failureNotice,
  briefFromDraft,
  FAILURE_ACTION,
  referenceText,
} from './draftReview';

// Drafting a phase with the generation service.
//
// The loop: ask, poll, review, edit, save. Generation takes minutes, so the
// server answers 202 and this polls — there is no version of this that waits on
// a request. Each poll is a short GET, so the same flow survives the iOS shell
// on a mobile network: a dropped request costs one poll, not the generation.
//
// The review is the point. A generated phase is never written straight into the
// programme: every exercise has to match the library before it can be saved, and
// the coach sees every warning against the line it belongs to.

const POLL_INTERVAL_MS = 2000;

const ProgramDrafts = ({ programId, exercises, onCommitted }) => {
  const { token } = useAuth();

  const [drafts, setDrafts] = useState([]);
  const [active, setActive] = useState(null);
  const [brief, setBrief] = useState(emptyBrief);
  const [showBrief, setShowBrief] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  // Which sequence policy answers "Suggest from history". '' is the default.
  const [sequence, setSequence] = useState('');
  // The policy line of the last suggestion, shown beside the brief it filled.
  const [sequenceLine, setSequenceLine] = useState('');
  const [notice, setNotice] = useState(null);
  const [edited, setEdited] = useState(null);
  // Consecutive failed polls of the active draft. See POLL_FAILURES_BEFORE_NOTICE.
  const [pollFailures, setPollFailures] = useState(0);

  // The poll timer, cleared on unmount so a component that has gone away does
  // not keep asking.
  const timer = useRef(null);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const fetchDrafts = useCallback(async () => {
    try {
      const response = await axios.get(
        buildApiUrl(`/programs/${programId}/drafts`),
        authConfig()
      );
      setDrafts(response.data?.drafts ?? []);
    } catch (error) {
      // A 404 means drafting is not configured on this server, which is a
      // supported state — the panel simply offers nothing.
      setDrafts([]);
    }
  }, [programId, authConfig]);

  useEffect(() => {
    fetchDrafts();
    return () => clearTimeout(timer.current);
  }, [fetchDrafts]);

  // Poll while the active draft is still being written.
  //
  // A failed poll that could pass schedules another, backing off, rather than
  // stopping: the draft keeps generating server-side whatever happens to one
  // request. One that cannot (an expired session, a deleted draft) stops and
  // says so — see pollFailure.
  useEffect(() => {
    if (!active || !isPending(active)) return undefined;

    timer.current = setTimeout(async () => {
      try {
        const response = await axios.get(
          buildApiUrl(`/programs/${programId}/drafts/${active.id}`),
          authConfig()
        );
        // The notice was about reaching the server; it is answered now.
        if (pollFailures >= POLL_FAILURES_BEFORE_NOTICE) setNotice(null);
        setPollFailures(0);
        setActive(response.data);
        if (!isPending(response.data)) {
          setEdited(response.data.draft ?? null);
          fetchDrafts();
        }
      } catch (error) {
        const failures = pollFailures + 1;
        const outcome = pollFailure(error, failures);
        if (outcome.notice) setNotice(outcome.notice);
        // Not changing pollFailures is what stops the loop: the effect re-runs
        // only when it, or the draft, changes.
        if (outcome.retry) setPollFailures(failures);
      }
    }, nextPollDelay(POLL_INTERVAL_MS, pollFailures));

    return () => clearTimeout(timer.current);
  }, [active, pollFailures, programId, authConfig, fetchDrafts]);

  const startDraft = async (event) => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      const response = await axios.post(
        buildApiUrl(`/programs/${programId}/drafts`),
        // The whole brief, not only what the form renders: a recommendation
        // supplies focus, training age, volume tier and equipment, and sending
        // just the four visible fields threw all of that away.
        briefRequest(brief),
        authConfig()
      );
      setPollFailures(0);
      setActive(response.data);
      setEdited(null);
      setShowBrief(false);
      fetchDrafts();
    } catch (error) {
      setNotice({
        tone: 'error',
        message: error.response?.data?.error || 'Could not start a draft.',
      });
    } finally {
      setBusy(false);
    }
  };

  // Ask the pipeline what this athlete should train next.
  //
  // Synchronous, unlike generation: this is progression analysis over what the
  // athlete has already done, not a model writing a programme, so the coach
  // waits for it in the form rather than polling a draft row.
  const suggestBrief = async () => {
    setSuggesting(true);
    setNotice(null);
    try {
      const response = await axios.post(
        buildApiUrl(`/programs/${programId}/drafts/recommend`),
        recommendRequest(sequence),
        authConfig()
      );
      setBrief((current) => mergeRecommendation(current, response.data));
      setSequenceLine(sequenceReasoningLine(response.data?.reasoning));
      setNotice({
        tone: 'info',
        message: 'Filled in from this athlete\u2019s previous blocks. Change anything you disagree with.',
      });
    } catch (error) {
      // The brief is left exactly as it was. A refusal is shown, and no other
      // sequence is asked in its place.
      setSequenceLine('');
      setNotice(recommendationNotice(error));
    } finally {
      setSuggesting(false);
    }
  };

  const openDraft = async (id) => {
    setNotice(null);
    try {
      const response = await axios.get(
        buildApiUrl(`/programs/${programId}/drafts/${id}`),
        authConfig()
      );
      setPollFailures(0);
      setActive(response.data);
      setEdited(response.data.draft ?? null);
    } catch (error) {
      setNotice({ tone: 'error', message: 'Could not open that draft.' });
    }
  };

  // Run a failed draft's stored brief again, on the same draft. The server
  // answers 202 with it pending, and the poll above takes it from there.
  const retryDraft = async () => {
    if (!active) return;
    setBusy(true);
    setNotice(null);
    try {
      const response = await axios.post(
        buildApiUrl(`/programs/${programId}/drafts/${active.id}/retry`),
        {},
        authConfig()
      );
      setPollFailures(0);
      setActive(response.data);
      setEdited(null);
      fetchDrafts();
    } catch (error) {
      setNotice({
        tone: 'error',
        message: error.response?.data?.error || 'Could not retry the draft.',
      });
    } finally {
      setBusy(false);
    }
  };

  // Reopen the brief form with what this draft asked for, so a refused brief
  // is corrected rather than retyped.
  const editBrief = () => {
    if (!active) return;
    setBrief(briefFromDraft(active));
    setSequenceLine('');
    setNotice(null);
    setActive(null);
    setEdited(null);
    setShowBrief(true);
  };

  // Change one line of the draft being reviewed.
  const editLine = (sessionIndex, lineIndex, field, value) => {
    setEdited((prev) => {
      if (!prev) return prev;
      const sessions = prev.sessions.map((session, si) => {
        if (si !== sessionIndex) return session;
        return {
          ...session,
          lines: session.lines.map((line, li) =>
            li === lineIndex ? { ...line, [field]: value } : line
          ),
        };
      });
      return { ...prev, sessions };
    });
  };

  const saveEdits = async () => {
    if (!active || !edited) return null;
    setBusy(true);
    setNotice(null);
    try {
      const response = await axios.put(
        buildApiUrl(`/programs/${programId}/drafts/${active.id}`),
        buildDraftUpdate(edited),
        authConfig()
      );
      setActive(response.data);
      setEdited(response.data.draft ?? null);
      return response.data;
    } catch (error) {
      setNotice({
        tone: 'error',
        message: error.response?.data?.error || 'Could not save your changes.',
      });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    // Edits are saved first, so what is committed is what is on screen. Without
    // this, picking a replacement exercise and pressing Save would commit the
    // draft as it was generated.
    const saved = await saveEdits();
    if (!saved || !saved.committable) {
      if (saved && !saved.committable) {
        setNotice({
          tone: 'error',
          message: 'Every exercise has to be matched to the library first.',
        });
      }
      return;
    }

    setBusy(true);
    try {
      await axios.post(
        buildApiUrl(`/programs/${programId}/drafts/${active.id}/commit`),
        {},
        authConfig()
      );
      setNotice({ tone: 'success', message: 'The phase was added to your programme.' });
      setActive(null);
      setEdited(null);
      fetchDrafts();
      if (onCommitted) onCommitted();
    } catch (error) {
      setNotice({
        tone: 'error',
        message: error.response?.data?.error || 'Could not save the phase.',
      });
    } finally {
      setBusy(false);
    }
  };

  const status = active ? statusLabel(active) : null;
  const failure = failureNotice(active);
  // The recommended fields the form has no input for, rendered read-only.
  const carried = carriedBriefFields(brief);
  const position = describeSqPosition(brief.sqPosition);
  const warnings = indexWarnings(edited?.warnings ?? active?.draft?.warnings);
  const summary = active ? reviewSummary({ ...active, draft: edited ?? active.draft }) : null;

  return (
    <section className="program-drafts">
      <div className="program-drafts-head">
        <h2 className="section-title">Draft a phase</h2>
        {!showBrief && !active && (
          <button type="button" className="btn-ghost" onClick={() => setShowBrief(true)}>
            Draft with AI
          </button>
        )}
      </div>

      {notice && (
        <p className={noticeClass(notice)} role="status">
          {notice.message}
        </p>
      )}

      {showBrief && (
        <form className="draft-brief" onSubmit={startDraft}>
          <div className="draft-brief-row">
            <label htmlFor="draft-phase">Phase name</label>
            <input
              id="draft-phase"
              value={brief.phase}
              onChange={(e) => setBrief(editBriefField(brief, 'phase', e.target.value))}
              placeholder="Accumulation"
            />
          </div>
          <div className="draft-brief-row">
            <label htmlFor="draft-sq">Strength quality</label>
            <select
              id="draft-sq"
              value={brief.sq}
              onChange={(e) => setBrief(editBriefField(brief, 'sq', e.target.value))}
            >
              <option value="">Let the model choose</option>
              <option value="H">Hypertrophy</option>
              <option value="FH">Functional Hypertrophy</option>
              <option value="RS">Relative Strength</option>
              <option value="SE">Strength Endurance</option>
              <option value="ES">Energy System</option>
            </select>
          </div>
          <div className="draft-brief-row">
            <label htmlFor="draft-sessions">Days per week</label>
            <input
              id="draft-sessions"
              type="number"
              min="2"
              max="6"
              value={brief.sessions}
              onChange={(e) => setBrief({ ...brief, sessions: e.target.value })}
            />
          </div>
          <div className="draft-brief-row">
            <label htmlFor="draft-notes">Notes for the model</label>
            <input
              id="draft-notes"
              value={brief.notes}
              onChange={(e) => setBrief({ ...brief, notes: e.target.value })}
              placeholder="Left knee, avoid deep flexion"
            />
          </div>
          {carried.length > 0 && (
            /* Read-only, and shown because they are SENT. A recommended field
               that travels to the model without appearing anywhere is a
               decision made on the coach's behalf and attributed to them.
               One wrapping paragraph in a class this component already uses:
               a new block element here is new layout, and layout claims in this
               repository are only true once a browser has agreed with them. */
            <p className="draft-summary muted">
              {carried.map((field) => `${field.label}: ${field.value}`).join(' · ')}
            </p>
          )}
          {(sequenceLine || position) && (
            /* The policy that chose this block, and where in the quality's rep
               spectrum it sits. Same paragraph class as the carried fields, for
               the same layout reason. */
            <p className="draft-summary muted" data-testid="draft-sequence">
              {[sequenceLine, position && `SQ position: ${position}`].filter(Boolean).join(' · ')}
            </p>
          )}
          <div className="draft-brief-row">
            <label htmlFor="draft-sequence">Sequence</label>
            <select
              id="draft-sequence"
              value={sequence}
              onChange={(e) => setSequence(e.target.value)}
            >
              {SEQUENCE_OPTIONS.map((option) => (
                <option key={option.value || 'default'} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="draft-brief-actions">
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Starting…' : 'Write the phase'}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={suggestBrief}
              disabled={suggesting || busy}
            >
              {suggesting ? 'Asking…' : 'Suggest from history'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setShowBrief(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {active && (
        <div className="draft-review">
          {failure ? (
            /* A failed draft says what happened, what it means, and offers the
               one action that can work: Retry for a transient failure, Edit
               brief for a refusal. The classes are the ones the ready review
               already renders with, and the wrapper carries no style of its own,
               so this adds no layout a browser has not already agreed with. */
            <div data-testid="draft-failure">
              <p className="draft-status draft-status-error" role="status" aria-live="polite">
                <strong>{failure.headline}</strong> {failure.reason}
              </p>
              <p className="draft-summary muted">{failure.hint}</p>
              {referenceText(active) && (
                <p className="draft-summary muted">{referenceText(active)}</p>
              )}
              <div className="draft-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={failure.action === FAILURE_ACTION.EDIT_BRIEF ? editBrief : retryDraft}
                  disabled={busy}
                >
                  {busy && failure.action === FAILURE_ACTION.RETRY ? 'Retrying…' : failure.actionLabel}
                </button>
                <button type="button" className="btn-ghost" onClick={() => { setActive(null); setEdited(null); }}>
                  Close
                </button>
              </div>
            </div>
          ) : (
            <p className={`draft-status draft-status-${status.tone}`} role="status" aria-live="polite">
              {status.text}
              {isPending(active) && <span className="muted"> {pendingHint(active)}</span>}
            </p>
          )}

          {edited && (
            <>
              <p className="draft-summary">
                <strong>{edited.phaseName}</strong>
                <span className="muted">
                  {' '}{edited.sessions.length} day{edited.sessions.length === 1 ? '' : 's'}
                  {' · '}{countLines({ draft: edited })} exercises
                </span>
              </p>
              <p className={summary.committable ? 'draft-summary muted' : 'draft-summary blocking'}>
                {summary.text}
              </p>

              {warnings.general.length > 0 && (
                <ul className="draft-general-warnings">
                  {warnings.general.map((warning, i) => (
                    <li key={i}>{warning.message}</li>
                  ))}
                </ul>
              )}

              {edited.sessions.map((session, sessionIndex) => (
                <div key={session.name + sessionIndex} className="draft-day">
                  <h3>{session.name}</h3>
                  {/* The TABLE scrolls, not the page. Seven columns do not fit in
                      375px, and without this the whole document scrolls sideways:
                      the intensity box lands off-screen entirely and the exercise
                      name — the thing a coach is actually reading — gets crushed
                      to 72px and wraps to six lines. */}
                  <div className="draft-table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Series</th>
                        <th scope="col">Exercise</th>
                        <th scope="col">Sets</th>
                        <th scope="col">Reps</th>
                        <th scope="col">Tempo</th>
                        <th scope="col">Rest</th>
                        <th scope="col">%1RM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {session.lines.map((line, lineIndex) => {
                        const lineWarnings = warningsForLine(warnings, session.name, line.series);
                        const unresolved = !line.exerciseId;
                        return (
                        <React.Fragment key={line.series + lineIndex}>
                          <tr className={unresolved ? 'unresolved' : ''}>
                            <td className="mono">{line.series}</td>
                            <td>
                              {unresolved ? (
                                <>
                                  {/* An unmatched name is replaced by PICKING from
                                      the library, never by creating a row for it. */}
                                  <span className="draft-missing">{line.exerciseName}</span>
                                  <label>
                                    <span className="sr-only">
                                      Replace {line.exerciseName} in {session.name}
                                    </span>
                                    <select
                                      value={line.exerciseId || ''}
                                      onChange={(e) =>
                                        editLine(sessionIndex, lineIndex, 'exerciseId', e.target.value)
                                      }
                                    >
                                      <option value="">Pick an exercise…</option>
                                      {(exercises ?? []).map((exercise) => (
                                        <option key={exercise.id} value={exercise.id}>
                                          {exercise.name}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                </>
                              ) : (
                                line.exerciseName
                              )}
                            </td>
                            <td className="mono">{line.sets}</td>
                            <td className="mono">{line.repScheme}</td>
                            <td className="mono">{line.tempo}</td>
                            <td className="mono">{line.rest}s</td>
                            {/* The one field on this row a coach is expected to
                                CHANGE rather than accept. The pipeline proposes a
                                percentage from the block's strength quality; only
                                the coach knows what this athlete is ready for.
                                Empty is a real answer — a corrective has no 1RM to
                                take a percentage of. */}
                            <td>
                              <label>
                                <span className="sr-only">
                                  Intensity for {line.exerciseName} in {session.name}
                                </span>
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="any"
                                  min="1"
                                  max="150"
                                  className="draft-percent mono"
                                  placeholder="—"
                                  value={line.percentOneRM ?? ''}
                                  onChange={(e) =>
                                    editLine(sessionIndex, lineIndex, 'percentOneRM', e.target.value)
                                  }
                                />
                              </label>
                            </td>
                            {lineWarnings.length > 0 && (
                              <td className="draft-warning">
                                {lineWarnings.map((w, i) => (
                                  <span key={i}>{w.message}</span>
                                ))}
                              </td>
                            )}
                          </tr>
                          {/* A sentence under the row, not a column beside it.
                              As a seventh cell it wrapped "work up 72-78% across
                              the four sets" into eight lines and tripled the
                              height of every row it appeared on. */}
                          {line.notes && (
                            <tr className="draft-notes-row">
                              <td colSpan={7} className="draft-line-notes">{line.notes}</td>
                            </tr>
                          )}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              ))}

              <div className="draft-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={commit}
                  disabled={busy || !summary.committable}
                >
                  {busy ? 'Saving…' : 'Save as a phase'}
                </button>
                <button type="button" className="btn-ghost" onClick={() => { setActive(null); setEdited(null); }}>
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {!active && drafts.length > 0 && (
        <ul className="draft-list">
          {drafts.map((draft) => (
            <li key={draft.id}>
              <button type="button" className="btn-ghost" onClick={() => openDraft(draft.id)}>
                Draft #{draft.id} — {statusLabel(draft).text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ProgramDrafts;
